import {
  Datapoint,
  ExampleMetric,
  MOCK_END_TIME,
  MOCK_START_TIME_SIX_HOURS_EARLIER,
  MockTimeSeriesResponseWithRealisticSeriesArgs,
  RandomNumberGenerator,
  SeriesData,
} from './model';

export function mockRealisticTimeSeriesData({
  startTimeMs = MOCK_START_TIME_SIX_HOURS_EARLIER,
  endTimeMs = MOCK_END_TIME,
  totalSeries = 25,
  totalDatapoints = 1000,
  stepMs,
  seed = 42,
  yVariationMultiplier = 12,
  randomnessMultiplier = 50,
  outlierRatio = 0.06,
  outlierMultiplier = 1.05,
  sparseRatio = 1.0,
}: MockTimeSeriesResponseWithRealisticSeriesArgs) {
  const startTimeS = Math.floor(startTimeMs / 1000);
  const endTimeS = Math.floor(endTimeMs / 1000);
  const timeRangeS = endTimeS - startTimeS;
  const stepSize = stepMs !== undefined ? stepMs / 1000 : timeRangeS / totalDatapoints;
  const mockData: SeriesData[] = [];

  const rng = createRandomNumberGenerator(seed);

  const exampleJobs = ['node', 'caddy', 'mysql', 'nginx', 'java-app', 'python-app', 'redis', 'kafka'];
  const exampleMetric: string[] = [
    'rpc_durations_histogram_seconds_bucket',
    'caddy_http_request_duration_seconds_bucket',
    'mysql_query_duration_seconds',
    'nginx_http_request_duration_seconds',
    'java_gc_duration_seconds',
    'python_gc_duration_seconds',
    'redis_command_duration_seconds',
    'kafka_consumer_lag_seconds',
    'error',
  ];
  const exampleInstances = Array.from({ length: 125 }, (_, i) => `instance-${i}.example.com:9090`);
  const numOutliers = Math.floor(totalSeries * outlierRatio);
  const outlierIndices = new Set(Array.from({ length: numOutliers }, () => rng.nextRange(0, totalSeries)));

  for (let i = 0; i < totalSeries; i++) {
    const jobIndex = rng.nextRange(0, exampleJobs.length);
    const metricIndex = rng.nextRange(0, exampleMetric.length);
    const instanceIndex = rng.nextRange(0, exampleInstances.length);
    const leValue = rng.nextRange(1, 11);

    const metric: ExampleMetric = {
      __name__: exampleMetric[metricIndex] ?? '',
      instance: exampleInstances[instanceIndex] ?? '',
      job: exampleJobs[jobIndex] ?? '',
      le: `${leValue}`,
    };

    const values: Datapoint[] = [];
    for (let j = 0; j < totalDatapoints; j++) {
      if (rng.nextFloat() <= sparseRatio) {
        const timestamp = startTimeS + j * stepSize;
        const seasonality = yVariationMultiplier * Math.sin((2.5 * Math.PI * j) / totalDatapoints);
        const randomness = randomnessMultiplier * (rng.nextFloat() - 0.5);
        let value = 1000 + seasonality + randomness;

        if (outlierIndices.has(i)) {
          const direction = rng.nextFloat() > 0.5 ? 1 : -1;
          value = Math.max(0, value * (1 + (outlierMultiplier - 1) * direction));
        }

        values.push([timestamp, String(value)]);
      }
    }
    mockData.push({ metric, values });
  }

  return mockData;
}

export function createRandomNumberGenerator(seed: number): RandomNumberGenerator {
  let state = seed;

  const m = 0x80000000; // 2**31
  const a = 1103515245;
  const c = 12345;

  const nextInt = () => {
    state = (a * state + c) % m;
    return state;
  };

  const nextFloat = () => nextInt() / (m - 1);

  const nextRange = (start: number, end: number) => {
    const rangeSize = end - start;
    const randomUnder1 = nextInt() / m;
    return start + Math.floor(randomUnder1 * rangeSize);
  };

  return { nextInt, nextFloat, nextRange };
}
